import { useEffect, useState, useCallback, Suspense } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useBaaSAuth } from '@/contexts/BaaSAuthContext';
import { Loader2, Banknote, LogOut, Shield, RefreshCw } from 'lucide-react';
import { BaaSNotificationBell } from '@/components/baas/BaaSNotificationBell';
import { Button } from '@/components/ui/button';
import { BaaSSidebar } from '@/components/baas/BaaSSidebar';
import { BaaSRouteGuard } from '@/components/baas/BaaSRouteGuard';
import { useBaaSSessionTimeout } from '@/hooks/useBaaSSessionTimeout';
import SessionTimeoutCountdown from '@/components/SessionTimeoutCountdown';
import { Skeleton } from '@/components/ui/skeleton';

function PageLoadingFallback() {
  return (
    <div className="p-6 space-y-4 flex-1">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-4 w-96 opacity-60" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
      <Skeleton className="h-64 rounded-xl mt-4" />
    </div>
  );
}

export default function BaaSLayout() {
  const { user, baasUser, loading, baasUserLoaded, signOut } = useBaaSAuth();
  const { showCountdown, countdownSeconds, resetTimer } = useBaaSSessionTimeout();
  const navigate = useNavigate();
  const [waitedTooLong, setWaitedTooLong] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate('/baas/login', { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!loading && baasUserLoaded && user && !baasUser) {
      const t = setTimeout(() => setWaitedTooLong(true), 8000);
      return () => clearTimeout(t);
    }
    setWaitedTooLong(false);
  }, [loading, baasUserLoaded, user, baasUser]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/baas/login', { replace: true });
  }, [signOut, navigate]);

  // FIX 2: Only show full-page loading spinner on first load (no session yet).
  // If we already have a user/session, skip the loading state so the shell
  // renders immediately and only the content area suspends.
  if ((loading || !baasUserLoaded) && !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Banknote className="w-10 h-10 text-accent opacity-60" />
          <Loader2 className="w-7 h-7 text-accent animate-spin" />
          <p className="text-muted-foreground text-sm">Loading BaaS portal...</p>
        </div>
      </div>
    );
  }

  if (!baasUser && baasUserLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-6 text-center max-w-md">
          <div className="rounded-full bg-destructive/10 p-6">
            <Shield className="w-12 h-12 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground">
            {waitedTooLong
              ? 'Unable to load your BaaS partner profile. Please try again or contact your relationship manager.'
              : 'Your account does not have BaaS Partner Portal access.'}
          </p>
          <div className="flex gap-3">
            {waitedTooLong && (
              <Button variant="outline" onClick={() => window.location.reload()}>
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
            )}
            <Button variant="outline" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // While baasUser is still loading but we know we have a session, render the
  // shell with a skeleton placeholder so the sidebar/header never disappear.
  const contentReady = !!baasUser;

  return (
    <>
      {showCountdown && (
        <SessionTimeoutCountdown
          secondsLeft={countdownSeconds}
          onStayLoggedIn={resetTimer}
          portalName="BaaS Partner"
        />
      )}
      <div className="min-h-screen bg-background flex flex-col">
        {/* Top bar — always rendered once user session exists */}
        <header className="h-14 border-b border-border/50 bg-card/60 flex items-center px-4 gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center">
              <Banknote className="w-4 h-4 text-accent" />
            </div>
            <span className="text-sm font-semibold text-foreground">BaaS Partner Portal</span>
          </div>
          <div className="flex-1" />
          {baasUser && <span className="text-xs text-muted-foreground hidden sm:block">{baasUser.partnerName}</span>}
          <BaaSNotificationBell />
          <Button variant="ghost" size="sm" onClick={handleSignOut} className="text-muted-foreground">
            <LogOut className="w-4 h-4" />
          </Button>
        </header>
        <div className="flex flex-1 overflow-hidden">
          <BaaSSidebar />
          {/* FIX 3: Nested Suspense boundary — only the content area suspends.
              The sidebar + header remain mounted during lazy chunk loading. */}
          <main className="flex-1 overflow-auto">
            {contentReady ? (
              <BaaSRouteGuard>
                <Suspense fallback={<PageLoadingFallback />}>
                  <div className="p-6">
                    <Outlet />
                  </div>
                </Suspense>
              </BaaSRouteGuard>
            ) : (
              <PageLoadingFallback />
            )}
          </main>
        </div>
      </div>
    </>
  );
}

